import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Box,
  Typography,
  IconButton,
  Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  LocationOn as LocationIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { AddressService } from '../../services/addressService';
import {
  CustomerAddress,
  CustomerAddressCreateData,
  CustomerAddressUpdateData,
  AddressType,
} from '../../types/address';

interface AddressFormModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  customerId: string;
  address?: CustomerAddress;
  mode: 'create' | 'edit';
}

const AddressFormModal: React.FC<AddressFormModalProps> = ({
  open,
  onClose,
  onSuccess,
  customerId,
  address,
  mode,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<CustomerAddressCreateData>({
    customer_id: customerId,
    address_type: AddressType.SHIPPING,
    is_default: false,
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'India',
    contact_person: '',
    contact_phone: '',
    gstin: '',
    is_active: true,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Initialize form data when address changes
  useEffect(() => {
    if (address && mode === 'edit') {
      setFormData({
        customer_id: address.customer_id,
        address_type: address.address_type,
        is_default: address.is_default,
        address_line1: address.address_line1,
        address_line2: address.address_line2 || '',
        city: address.city,
        state: address.state,
        postal_code: address.postal_code,
        country: address.country,
        contact_person: address.contact_person || '',
        contact_phone: address.contact_phone || '',
        gstin: address.gstin || '',
        is_active: address.is_active,
      });
    } else if (mode === 'create') {
      setFormData({
        customer_id: customerId,
        address_type: AddressType.SHIPPING,
        is_default: false,
        address_line1: '',
        address_line2: '',
        city: '',
        state: '',
        postal_code: '',
        country: 'India',
        contact_person: '',
        contact_phone: '',
        gstin: '',
        is_active: true,
      });
    }
    setErrors({});
  }, [address, mode, customerId]);

  const handleInputChange = (field: keyof CustomerAddressCreateData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.address_line1.trim()) {
      newErrors.address_line1 = 'Address line 1 is required';
    }

    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    }

    if (!formData.state.trim()) {
      newErrors.state = 'State is required';
    }

    if (!formData.postal_code.trim()) {
      newErrors.postal_code = 'Postal code is required';
    } else if (!/^\d{6}$/.test(formData.postal_code)) {
      newErrors.postal_code = 'Postal code must be 6 digits';
    }

    if (!formData.country.trim()) {
      newErrors.country = 'Country is required';
    }

    if (formData.contact_phone && !/^\d{10}$/.test(formData.contact_phone.replace(/\D/g, ''))) {
      newErrors.contact_phone = 'Contact phone must be 10 digits';
    }

    if (formData.gstin && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(formData.gstin)) {
      newErrors.gstin = 'Invalid GSTIN format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      if (mode === 'edit' && address) {
        const updateData: CustomerAddressUpdateData = { ...formData };
        delete (updateData as any).customer_id; // Remove customer_id from update data
        await AddressService.updateAddress(customerId, address.id, updateData);
        enqueueSnackbar('Address updated successfully', { variant: 'success' });
      } else {
        await AddressService.createAddress(formData);
        enqueueSnackbar('Address created successfully', { variant: 'success' });
      }
      
      // Close modal first
      onClose();
      
      // Then trigger refresh with a small delay to ensure backend commit completes
      setTimeout(() => {
        onSuccess();
      }, 100);
    } catch (error: any) {
      enqueueSnackbar(
        error?.response?.data?.detail || 'Failed to save address',
        { variant: 'error' }
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };



  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocationIcon color="primary" />
            <Typography variant="h6">
              {mode === 'edit' ? 'Edit Address' : 'Add New Address'}
            </Typography>
          </Box>
          <IconButton onClick={handleClose} disabled={loading}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 3 }}>
        <Grid container spacing={3}>
          {/* Address Type */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Address Type</InputLabel>
              <Select
                value={formData.address_type}
                onChange={(e) => handleInputChange('address_type', e.target.value)}
                label="Address Type"
              >
                <MenuItem value={AddressType.BILLING}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    Billing Address
                  </Box>
                </MenuItem>
                <MenuItem value={AddressType.SHIPPING}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    Shipping Address
                  </Box>
                </MenuItem>
                <MenuItem value={AddressType.BOTH}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    Billing & Shipping Address
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Default Address Toggle */}
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_default}
                    onChange={(e) => handleInputChange('is_default', e.target.checked)}
                    color="primary"
                  />
                }
                label="Set as default address"
              />
            </Box>
          </Grid>

          {/* Address Line 1 */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Address Line 1"
              value={formData.address_line1}
              onChange={(e) => handleInputChange('address_line1', e.target.value)}
              error={!!errors.address_line1}
              helperText={errors.address_line1}
              required
              placeholder="Building, Street, Area"
            />
          </Grid>

          {/* Address Line 2 */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Address Line 2"
              value={formData.address_line2}
              onChange={(e) => handleInputChange('address_line2', e.target.value)}
              placeholder="Landmark, Near (Optional)"
            />
          </Grid>

          {/* City */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="City"
              value={formData.city}
              onChange={(e) => handleInputChange('city', e.target.value)}
              error={!!errors.city}
              helperText={errors.city}
              required
            />
          </Grid>

          {/* State */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="State"
              value={formData.state}
              onChange={(e) => handleInputChange('state', e.target.value)}
              error={!!errors.state}
              helperText={errors.state}
              required
            />
          </Grid>

          {/* Postal Code */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Postal Code"
              value={formData.postal_code}
              onChange={(e) => handleInputChange('postal_code', e.target.value)}
              error={!!errors.postal_code}
              helperText={errors.postal_code}
              required
              inputProps={{ maxLength: 6 }}
            />
          </Grid>

          {/* Country */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Country"
              value={formData.country}
              onChange={(e) => handleInputChange('country', e.target.value)}
              error={!!errors.country}
              helperText={errors.country}
              required
            />
          </Grid>

          {/* Contact Person */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Contact Person"
              value={formData.contact_person}
              onChange={(e) => handleInputChange('contact_person', e.target.value)}
              placeholder="Name of contact person at this address"
            />
          </Grid>

          {/* Contact Phone */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Contact Phone"
              value={formData.contact_phone}
              onChange={(e) => handleInputChange('contact_phone', e.target.value)}
              error={!!errors.contact_phone}
              helperText={errors.contact_phone}
              placeholder="Phone number for this address"
            />
          </Grid>

          {/* GSTIN (for billing addresses) */}
          {(formData.address_type === AddressType.BILLING || formData.address_type === AddressType.BOTH) && (
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="GSTIN"
                value={formData.gstin}
                onChange={(e) => handleInputChange('gstin', e.target.value.toUpperCase())}
                error={!!errors.gstin}
                helperText={errors.gstin}
                placeholder="GST Identification Number"
              />
            </Grid>
          )}

          {/* Active Status */}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => handleInputChange('is_active', e.target.checked)}
                  color="primary"
                />
              }
              label="Address is active"
            />
          </Grid>
        </Grid>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 3 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          variant="outlined"
          sx={{ minWidth: 100 }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={loading}
          variant="contained"
          color="primary"
          startIcon={<SaveIcon />}
          sx={{ minWidth: 120 }}
        >
          {loading ? 'Saving...' : mode === 'edit' ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddressFormModal;